import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { BookingPurchaseDialogData } from './BookingPurchaseDialog.types';
import {
  buildPriceServices,
  canQuoteMembershipPrice,
  isCreditPack,
  isSingleMembership,
} from './buyMembershipRules';
import type {
  BuyMembershipHostData,
  MembershipPriceResult,
} from './buyMembership.types';
import { t } from '../../services/locale';

function promoErrorCode(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return 'PROMO_CODE_INVALID_OR_NOT_FOUND_ERROR';
  }
  const details = error as { message_code?: string; code?: string };
  const code = details.message_code || details.code;
  return !code || code === 'record not found'
    ? 'PROMO_CODE_INVALID_OR_NOT_FOUND_ERROR'
    : code;
}

function hasOnlyFutureDiscount(result: MembershipPriceResult): boolean {
  return Boolean(result.appliedFutureDiscounts?.length) &&
    !result.appliedDiscounts?.length;
}

function promoSuccessKey(
  result: MembershipPriceResult,
  joiningFeeOnly: boolean
): string {
  if (hasOnlyFutureDiscount(result)) {
    return 'WEB_PROMO_CODE_APPLIED_SUCCESSFULLY_FUTURE';
  }
  return joiningFeeOnly
    ? 'WEB_PROMO_CODE_APPLIED_SUCCESSFULLY_JOINING_FEE'
    : 'WEB_PROMO_CODE_APPLIED_SUCCESSFULLY';
}

function isPromoVisible(data: BuyMembershipHostData): boolean {
  const plan = data.membership.planToBuy;
  if ((plan.price || 0) <= 0) return false;
  return isCreditPack(plan)
    ? data.flags.creditPackPromoEnabled
    : data.flags.membershipPromoEnabled;
}

type PromoHandlers = {
  changePromo: (value: string) => void;
  applyPromo: () => void;
  clearPromo: () => void;
};

function isJoiningFeeOnlyDiscount(
  data: BuyMembershipHostData,
  result: MembershipPriceResult
): boolean {
  const plan = data.membership.planToBuy;
  return Boolean(
    data.flags.joiningFeeDiscountEnabled &&
      !isSingleMembership(plan) &&
      !isCreditPack(plan) &&
      result.appliedDiscounts?.some(
        (discount) => discount.applies_to_joining_fee_only === true
      )
  );
}

function successfulBreakdown(args: {
  data: BuyMembershipHostData;
  result: MembershipPriceResult;
  nextPromo?: string;
  promoDraft: string;
  promoVisible: boolean;
  handlers: PromoHandlers;
}): BookingPurchaseDialogData['priceBreakdown'] {
  const { data, result, nextPromo, promoDraft, promoVisible, handlers } = args;
  const successKey = promoSuccessKey(
    result,
    isJoiningFeeOnlyDiscount(data, result)
  );
  return {
    rows: result.rows,
    footnotes: result.footnotes,
    promo: promoVisible
      ? {
          visible: true,
          value: nextPromo || promoDraft,
          submitted: Boolean(nextPromo),
          applyDisabled: Boolean(nextPromo) || promoDraft.length < 3,
          appliedSuccessfully: Boolean(nextPromo),
          successMessage: nextPromo ? t(successKey) : null,
          placeholder: t('WEB_ENTER_PROMO_CODE_HERE'),
          applyLabel: t('WEB_APPLY_PROMO_CODE'),
          onChange: handlers.changePromo,
          onApply: handlers.applyPromo,
          onClear: handlers.clearPromo,
        }
      : null,
  };
}

function failedBreakdown(args: {
  current: BookingPurchaseDialogData['priceBreakdown'];
  data: BuyMembershipHostData;
  error: unknown;
  nextPromo?: string;
  promoDraft: string;
  promoVisible: boolean;
  handlers: PromoHandlers;
}): BookingPurchaseDialogData['priceBreakdown'] {
  const { current, data, error, nextPromo, promoDraft, promoVisible, handlers } = args;
  return {
    rows: nextPromo ? current?.rows : [],
    footnotes: nextPromo ? current?.footnotes : [],
    error: nextPromo ? null : t('WEB_FAILED_TO_FETCH_TAX_BREAKDOWN'),
    promo: promoVisible
      ? {
          visible: true,
          value: nextPromo || promoDraft,
          submitted: Boolean(nextPromo),
          applicationError: nextPromo ? t(promoErrorCode(error)) : null,
          placeholder: t('WEB_ENTER_PROMO_CODE_HERE'),
          applyLabel: t('WEB_APPLY_PROMO_CODE'),
          onChange: handlers.changePromo,
          onApply: handlers.applyPromo,
          onClear: handlers.clearPromo,
        }
      : null,
  };
}

function changePromoDraft(
  value: string,
  promoDraftRef: { current: string },
  setPromoDraft: (value: string) => void,
  setPriceBreakdown: Dispatch<
    SetStateAction<BookingPurchaseDialogData['priceBreakdown']>
  >
) {
  const normalized = value.toUpperCase().trim();
  promoDraftRef.current = normalized;
  setPromoDraft(normalized);
  setPriceBreakdown((current) => {
    if (!current?.promo) return current;
    return {
      ...current,
      promo: {
        ...current.promo,
        value: normalized,
        applyDisabled: normalized.length < 3,
      },
    };
  });
}

function applyDraftPromo(
  code: string,
  setPromoCode: (code: string) => void,
  refreshPrice: (promo?: string, date?: string) => Promise<void>,
  selectedStartDate?: string
) {
  if (code.length < 3) return;
  setPromoCode(code);
  void refreshPrice(code, selectedStartDate);
}

export function useBuyMembershipPricing(
  data: BuyMembershipHostData,
  selectedStartDate?: string,
  onProRated?: () => void
) {
  const plan = data.membership.planToBuy;
  const [promoDraft, setPromoDraft] = useState('');
  const promoDraftRef = useRef('');
  const [promoCode, setPromoCode] = useState<string>();
  const [priceBreakdown, setPriceBreakdown] =
    useState<BookingPurchaseDialogData['priceBreakdown']>(null);
  const [totalPrice, setTotalPrice] = useState(plan.price || 0);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [isProRated, setIsProRated] = useState(false);
  const requestGeneration = useRef(0);
  const promoVisible = isPromoVisible(data);

  const refreshPrice = useCallback(
    async (nextPromo = promoCode, nextDate = selectedStartDate) => {
      const generation = ++requestGeneration.current;
      if (!canQuoteMembershipPrice(data)) {
        setPriceBreakdown(null);
        return;
      }
      setLoadingPrice(true);
      try {
        const result = await data.getPriceBreakdown(
          buildPriceServices(data, nextDate),
          nextPromo
        );
        if (generation !== requestGeneration.current) {
          return;
        }
        setTotalPrice(result.totalPrice);
        setIsProRated(result.isProRated);
        if (result.isProRated) {
          onProRated?.();
        }
        setPriceBreakdown(
          successfulBreakdown({
            data,
            result,
            nextPromo,
            promoDraft: promoDraftRef.current,
            promoVisible,
            handlers: { changePromo, applyPromo, clearPromo },
          })
        );
      } catch (error) {
        if (generation !== requestGeneration.current) {
          return;
        }
        setPriceBreakdown((current) =>
          failedBreakdown({
            current,
            data,
            error,
            nextPromo,
            promoDraft: promoDraftRef.current,
            promoVisible,
            handlers: { changePromo, applyPromo, clearPromo },
          })
        );
      } finally {
        if (generation === requestGeneration.current) {
          setLoadingPrice(false);
        }
      }
    },
    [data, onProRated, plan, promoCode, promoVisible, selectedStartDate]
  );

  const changePromo = useCallback((value: string) => {
    changePromoDraft(
      value,
      promoDraftRef,
      setPromoDraft,
      setPriceBreakdown
    );
  }, []);

  const applyPromo = useCallback(() => {
    applyDraftPromo(
      promoDraftRef.current,
      setPromoCode,
      refreshPrice,
      selectedStartDate
    );
  }, [refreshPrice, selectedStartDate]);

  const clearPromo = useCallback(() => {
    promoDraftRef.current = '';
    setPromoDraft('');
    setPromoCode(undefined);
    void refreshPrice('', selectedStartDate);
  }, [refreshPrice, selectedStartDate]);

  return {
    promoCode,
    priceBreakdown,
    totalPrice,
    loadingPrice,
    isProRated,
    refreshPrice,
  };
}
